import { createNotificationIfNotExists, hasRecentNotification } from '../api/notifications'
import { APP_URL } from '../lib/config'
import { getNotificationPreferences } from '../api/notifications'
import { showBrowserNotification } from './browserNotifications'
import { sendNotificationEmail } from '../api/email'
import { pb } from '../lib/pocketbase'
import type { Notification, NotificationType, NotificationPriority, NotificationEntityType, NotificationPreferences } from '../types/notifications'

interface ChannelConfig {
  email: boolean
  in_app: boolean
  browser: boolean
}

/**
 * Determine which channels are enabled for a specific notification type.
 * Combines master kill-switches with per-type channel preferences.
 */
function getChannelsForType(preferences: NotificationPreferences, type: NotificationType): ChannelConfig {
  const typeEnabled = getTypeEnabled(preferences, type)
  if (!typeEnabled) {
    return { email: false, in_app: false, browser: false }
  }

  switch (type) {
    case 'task':
      return {
        email: preferences.email_enabled && (preferences.task_email ?? true),
        in_app: preferences.in_app_enabled && (preferences.task_in_app ?? true),
        browser: preferences.browser_enabled,
      }
    case 'compliance':
      return {
        email: preferences.email_enabled && (preferences.compliance_email ?? true),
        in_app: preferences.in_app_enabled && (preferences.compliance_in_app ?? true),
        browser: preferences.browser_enabled,
      }
    case 'deal':
      return {
        email: preferences.email_enabled && (preferences.deal_email ?? true),
        in_app: preferences.in_app_enabled && (preferences.deal_in_app ?? true),
        browser: preferences.browser_enabled,
      }
    case 'event':
      return {
        email: preferences.email_enabled && (preferences.event_email ?? false),
        in_app: preferences.in_app_enabled && (preferences.event_in_app ?? true),
        browser: preferences.browser_enabled,
      }
    case 'message': // follow-ups
      return {
        email: preferences.email_enabled && (preferences.follow_up_email ?? false),
        in_app: preferences.in_app_enabled && (preferences.follow_up_in_app ?? true),
        browser: preferences.browser_enabled,
      }
    case 'contract':
      return {
        email: preferences.email_enabled && (preferences.contract_email ?? true),
        in_app: preferences.in_app_enabled && (preferences.contract_in_app ?? true),
        browser: preferences.browser_enabled,
      }
    // 'payment' has no producer and no toggle; it takes the default below.
    case 'system':
    default:
      return {
        email: preferences.email_enabled,
        in_app: preferences.in_app_enabled,
        browser: preferences.browser_enabled,
      }
  }
}

/**
 * Service to create and dispatch notifications based on user preferences
 */
export async function notifyUser(
  userId: string,
  agencyId: string,
  notification: {
    type: NotificationType
    title: string
    message?: string | null
    priority?: NotificationPriority | null
    action_url?: string | null
    related_entity_type?: NotificationEntityType | null
    related_entity_id?: string | null
  },
  dismissedEntities?: Set<string>,
  snoozedEntities?: Set<string>
) {
  if (!agencyId) {
    console.warn('notifyUser called without agencyId - skipping notification')
    return
  }

  try {
    const preferences = await getNotificationPreferences(userId)
    if (!preferences) {
      await createNotificationIfNotExists(
        { user_id: userId, agency_id: agencyId, ...notification },
        24,
        dismissedEntities,
        snoozedEntities
      )
      return
    }

    const channels = getChannelsForType(preferences, notification.type)

    // If no channels are enabled, skip entirely
    if (!channels.email && !channels.in_app && !channels.browser) return

    let isNewNotification = false

    // Follow-up reminders (type 'message') use a 7-day window so a stale contact
    // doesn't re-notify every day — once per week is enough.
    const dedupHours = notification.type === 'message' ? 168 : 24

    if (channels.in_app) {
      const created = await createNotificationIfNotExists(
        { user_id: userId, agency_id: agencyId, ...notification },
        dedupHours,
        dismissedEntities,
        snoozedEntities
      )
      isNewNotification = created !== null
    } else {
      // Even without in-app, check dedup before sending email/browser
      if (notification.related_entity_id) {
        const entityKey = `${notification.type}:${notification.related_entity_id}`
        if (dismissedEntities?.has(entityKey) || snoozedEntities?.has(entityKey)) return

        const exists = await hasRecentNotification(userId, notification.type, notification.related_entity_id, dedupHours)
        if (exists) return
      }
      isNewNotification = true
    }

    if (!isNewNotification) return

    // Browser notification
    if (channels.browser) {
      const fullNotification: Notification = {
        id: '',
        user_id: userId,
        agency_id: agencyId,
        type: notification.type,
        title: notification.title,
        message: notification.message ?? null,
        priority: notification.priority ?? null,
        action_url: notification.action_url ?? null,
        related_entity_type: notification.related_entity_type ?? null,
        related_entity_id: notification.related_entity_id ?? null,
        read: false,
        read_at: null,
        snoozed_until: null,
        dismissed: false,
        created: new Date().toISOString(),
        updated: new Date().toISOString(),
      }
      showBrowserNotification(fullNotification)
    }

    // Email notification
    if (channels.email) {
      try {
        const user = await pb.collection('users').getOne(userId)
        if (user?.email) {
          await sendNotificationEmail({
            to: user.email,
            subject: `[Reppr] ${notification.title}`,
            body: formatEmailBody(notification),
          })
        }
      } catch (emailErr) {
        console.error('Failed to send email notification:', emailErr)
      }
    }
  } catch (err) {
    console.error('Failed to notify user:', err)
  }
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

function formatEmailBody(notification: {
  title: string
  message?: string | null
  action_url?: string | null
  type: NotificationType
}): string {
  const typeLabels: Record<NotificationType, string> = {
    task: 'Task',
    deal: 'Deal',
    compliance: 'Compliance',
    event: 'Calendar Event',
    message: 'Message',
    payment: 'Payment',
    contract: 'Contract',
    system: 'System',
    injury: 'Injury',
    transaction: 'Transaction',
  }

  const baseUrl = APP_URL
  let fullActionUrl = notification.action_url
  if (fullActionUrl && fullActionUrl.startsWith('/')) {
    fullActionUrl = `${baseUrl}${fullActionUrl}`
  }

  const safeTitle = escapeHtml(notification.title)
  const safeMessage = notification.message ? escapeHtml(notification.message) : null
  const safeActionUrl = fullActionUrl ? escapeHtml(fullActionUrl) : null

  const body = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: #8A6A22; color: white; padding: 20px; border-radius: 8px 8px 0 0; }
    .content { background: #f8f9fa; padding: 20px; border: 1px solid #e9ecef; }
    .footer { background: #f1f3f5; padding: 15px; border-radius: 0 0 8px 8px; font-size: 12px; color: #666; }
    .badge { display: inline-block; background: #e9ecef; padding: 4px 8px; border-radius: 4px; font-size: 12px; }
    .button { display: inline-block; background: #8A6A22; color: white; padding: 12px 24px; border-radius: 6px; text-decoration: none; margin-top: 15px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h2 style="margin: 0;">${safeTitle}</h2>
    </div>
    <div class="content">
      <p><span class="badge">${typeLabels[notification.type] || 'Notification'}</span></p>
      ${safeMessage ? `<p>${safeMessage}</p>` : ''}
      ${safeActionUrl ? `<a href="${safeActionUrl}" class="button">View Details</a>` : ''}
    </div>
    <div class="footer">
      <p>This is an automated notification from Reppr Agency OS.</p>
      <p>You can manage your notification preferences in Settings.</p>
    </div>
  </div>
</body>
</html>
  `.trim()

  return body
}

/**
 * Check if a notification type is enabled based on user preferences
 */
function getTypeEnabled(preferences: any, type: NotificationType): boolean {
  switch (type) {
    case 'task':
      return (preferences.task_overdue ?? true) || (preferences.task_due_today ?? true)
    case 'compliance':
      return (preferences.compliance_overdue ?? true) || (preferences.compliance_due_today ?? true)
    case 'event':
      return preferences.event_upcoming ?? true
    case 'deal':
      return preferences.deal_stalled ?? true
    case 'contract':
      return preferences.contract_expiring ?? true
    case 'payment':
      // invoice_overdue was removed from the collection in Sep 2026: nothing
      // has ever produced a payment notification, so it gated nothing.
      return true
    case 'message':
      return preferences.follow_up_due ?? true
    case 'system':
    default:
      return true
  }
}
